import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { TemplateBoardIcon } from '@trello/nachos/icons/template-board';
import { Select } from '@trello/nachos/select';
import { RouterLink } from '@trello/router/router-link';

import type { TemplateTileProps } from './TemplateTile';
import { TemplateTile } from './TemplateTile';
import type {
  Category,
  LocalizedTemplatesListHash,
  TemplatesListHash,
} from './types';
import { Categories } from './types';

import styles from './TemplatePicker.less';

interface TemplatePickerProps
  extends Pick<TemplateTileProps, 'onTemplateClicked'> {
  onDismiss: () => void;
  templateIds: string[];
  selectedCategory: Category;
  isModernizedBoardPage?: boolean;
  onChangeCategory: (category: { value: Category; label: string }) => void;
}

const onExploreMoreTemplatesClick = () => {
  Analytics.sendClickedLinkEvent({
    linkName: 'exploreTemplatesLink',
    source: 'templatePickerSection',
  });
};

const categoryToStringKeyHash: Record<Category, string> = {
  popular: 'popular',
  business: 'small-business',
  education: 'education',
  engineering: 'engineering-it',
  marketing: 'marketing',
  'operations-hr': 'human-resources',
  operations: 'operations',
  'sales-crm': 'sales-crm',
  design: 'design',
};

// Selects the best localized template list based on the current locale and
// language. Returns an empty set if no locale can be found.
export const localizedTemplatesSelector = (
  templates: LocalizedTemplatesListHash,
  currentLocale: string,
  language: string,
): TemplatesListHash => {
  if (templates[currentLocale]) {
    return templates[currentLocale];
  } else if (templates[language]) {
    return templates[language];
  } else if (templates['en']) {
    return templates['en'];
  }

  // We should never reach here, but we do in environments that aren't staging
  // or prod.
  return Object.values(Categories).reduce((h, k) => {
    h[k] = [];
    return h;
  }, {} as TemplatesListHash);
};

export const TemplatePicker: FunctionComponent<TemplatePickerProps> = ({
  onDismiss,
  templateIds,
  onTemplateClicked,
  selectedCategory,
  isModernizedBoardPage,
  onChangeCategory,
}) => {
  const selectedValue = useMemo(
    () => ({
      value: selectedCategory,
      label: categoryToStringKeyHash[selectedCategory],
    }),
    [selectedCategory],
  );

  return (
    <div
      className={cx(styles.container, {
        [styles.fullscreen]: isModernizedBoardPage,
      })}
    >
      <div className={styles.headerRow}>
        <div className={styles.iconHeaderContainer}>
          <TemplateBoardIcon
            size="large"
            dangerous_className={styles.templateBoardIcon}
          />
          {selectedCategory === 'popular' ? (
            <FormattedMessage
              id="templates.templates_picker.most-popular-templates"
              defaultMessage="Most popular templates"
              description="The title of the section that displays popular templates"
              tagName="h2"
            />
          ) : (
            <FormattedMessage
              id={`templates.templates_picker.suggested-${categoryToStringKeyHash[selectedCategory]}`}
              defaultMessage="Templates"
              description="The title of the section that displays templates for a category"
              tagName="h2"
            />
          )}
        </div>
        <Button
          onClick={onDismiss}
          className={styles.dismissButton}
          iconBefore={<CloseIcon size="medium" />}
        />
      </div>
      <span className={styles.subheader}>
        <FormattedMessage
          id="templates.templates_picker.get-going-faster"
          defaultMessage="Get going faster with a template from the Trello community or {chooseACategoryLink}"
          description="A message indicating the user can either choose a template from the community or by searching in a category"
          values={{
            chooseACategoryLink: (
              <span className={styles.categorySelector} key="category-selector">
                <Select
                  placeholder={
                    <FormattedMessage
                      id="templates.templates_picker.choose-a-category"
                      defaultMessage="choose a category"
                      description="A description for the dropdown menu that allows users to choose a category"
                    />
                  }
                  value={selectedValue}
                  options={Object.keys(Categories).map((category) => ({
                    value: Categories[category as keyof typeof Categories],
                    label: intl.formatMessage({
                      id: `templates.templates_picker.${
                        categoryToStringKeyHash[
                          Categories[category as keyof typeof Categories]
                        ]
                      }`,
                      defaultMessage: 'Option',
                      description:
                        'An option from a select dropdown that chooses categories of templates',
                    }),
                  }))}
                  // @ts-expect-error
                  onChange={onChangeCategory}
                  controlShouldRenderValue={false}
                />
              </span>
            ),
          }}
        />
      </span>
      <div
        className={cx(
          styles.templatesContainer,
          isModernizedBoardPage && styles.modernized,
        )}
        data-testid={'templates-container'}
      >
        {templateIds.map((templateId) => (
          <TemplateTile
            key={templateId}
            boardId={templateId}
            onTemplateClicked={onTemplateClicked}
            isModernized={isModernizedBoardPage}
          />
        ))}
      </div>
      <p className={styles.templateGalleryLink}>
        <RouterLink href="/templates" onClick={onExploreMoreTemplatesClick}>
          <FormattedMessage
            id="templates.templates_picker.browse-the-full-template-gallery"
            defaultMessage="Browse the full template gallery"
            description="A description for the link that takes users to the full template gallery"
          />
        </RouterLink>
      </p>
    </div>
  );
};
